import type {
  RobotTagFilter,
  RobotTagFilterDto,
} from '@app/components/Modals/BotDetailsModal/types';
import type {
  GroupInnerLogic,
  MultiSelectGroup,
  MultiSelectOption,
  MultiSelectValue,
} from '@app/shared/components/MultiSelect/types';
import type { NTags } from '@app/types';

const DEFAULT_GROUP_ID = 'trigger-tags-default-group';

export const EMPTY_TRIGGER_TAGS: MultiSelectValue = {
  groups: [{ id: DEFAULT_GROUP_ID, logic: 'AND', selectedValues: [] }],
};

export const mapTagsToMultiSelectOptions = (
  tags: NTags.Tag[]
): MultiSelectOption[] =>
  tags.map(({ Id, Name, IsDeleted }) => ({
    label: Name?.trim() || `#${Id}`,
    value: String(Id),
    isDeleted: IsDeleted,
  }));

export const getTriggerTagsError = (
  value: MultiSelectValue | undefined
): string | null => {
  const groups = value?.groups ?? [];

  if (!groups.some(({ selectedValues }) => selectedValues.length)) {
    return 'Пожалуйста, выберите хотя бы один тег';
  }

  if (groups.some(({ selectedValues }) => !selectedValues.length)) {
    return 'Выберите теги в каждом списке или удалите пустой список';
  }

  return null;
};

// Поменялись ли выбранные теги или число списков. Смена И/ИЛИ/НЕТ сюда не
// входит: она не должна запускать валидацию ещё пустого списка.
export const isSelectionChanged = (
  prev: MultiSelectValue,
  next: MultiSelectValue
): boolean =>
  prev.groups.length !== next.groups.length ||
  next.groups.some(
    ({ selectedValues }, index) =>
      selectedValues.join() !== prev.groups[index]?.selectedValues.join()
  );

// Добавляет тег в первую группу (например, только что созданный через
// «Добавить тег»), не трогая остальные списки.
export const addTagToTriggerTags = (
  value: MultiSelectValue | undefined,
  tagId: number
): MultiSelectValue => {
  const [first, ...rest] = value?.groups?.length
    ? value.groups
    : EMPTY_TRIGGER_TAGS.groups;
  const tagValue = String(tagId);

  return {
    groups: [
      {
        ...first,
        selectedValues: first.selectedValues.includes(tagValue)
          ? first.selectedValues
          : [...first.selectedValues, tagValue],
      },
      ...rest,
    ],
  };
};

const innerIncludeToLogic = (innerInclude: boolean | null): GroupInnerLogic =>
  innerInclude === null ? 'NOT' : innerInclude ? 'AND' : 'OR';

const logicToInnerInclude = (logic: GroupInnerLogic): boolean | null =>
  logic === 'NOT' ? null : logic === 'AND';

// У роботов, сохранённых до появления списков, TagFilters пустой — тогда
// собираем один список из старых Tags / HasOrRelation. При пересохранении
// бэкенд сам переводит робота на новый формат.
export const mapTriggerTagsFromApi = ({
  TagFilters,
  Tags,
  HasOrRelation,
}: {
  TagFilters?: RobotTagFilter[] | null;
  Tags?: number[] | null;
  HasOrRelation?: boolean | null;
}): MultiSelectValue => {
  if (!TagFilters?.length) {
    return {
      groups: [
        {
          id: DEFAULT_GROUP_ID,
          logic: HasOrRelation ? 'OR' : 'AND',
          selectedValues: (Tags ?? []).map(String),
        },
      ],
    };
  }

  return {
    groups: TagFilters.map(
      ({ OuterInclude, InnerInclude, TagIds }, index): MultiSelectGroup => ({
        id: `${DEFAULT_GROUP_ID}-${index}`,
        logic: innerIncludeToLogic(InnerInclude),
        relationToPrev: index === 0 ? undefined : OuterInclude ? 'AND' : 'OR',
        selectedValues: TagIds.map(String),
      })
    ),
  };
};

export const mapTriggerTagsToApi = (
  value: MultiSelectValue | undefined
): RobotTagFilter[] =>
  (value?.groups ?? [])
    .filter(({ selectedValues }) => selectedValues.length)
    .map(({ logic, relationToPrev, selectedValues }, index) => ({
      // Для первого списка бэкенд связку игнорирует.
      OuterInclude: index === 0 || relationToPrev !== 'OR',
      InnerInclude: logicToInnerInclude(logic),
      TagIds: selectedValues.map(Number),
    }));

// Подпись условий для карточки робота: «(A ИЛИ B) И НЕТ (#СМС)».
export const formatTriggerTagFilters = (filters: RobotTagFilterDto[]): string =>
  filters
    .map(({ OuterInclude, InnerInclude, TagIds, TagNames }, index) => {
      const names = TagNames?.length ? TagNames : TagIds.map((id) => `#${id}`);
      const list =
        InnerInclude === null
          ? `НЕТ (${names.join(', ')})`
          : names.join(InnerInclude ? ' И ' : ' ИЛИ ');
      const wrappedList =
        filters.length > 1 && InnerInclude !== null && names.length > 1
          ? `(${list})`
          : list;

      if (index === 0) return wrappedList;

      return `${OuterInclude ? 'И' : 'ИЛИ'} ${wrappedList}`;
    })
    .join(' ');
